<script setup lang="ts">
import type { MaybeArray } from '~/types/utils'
import { isSafari } from '~/utils/entity-checks'

type VideoType = 'video/mp4' | 'video/webm'

interface VVideoProps {
  videos: MaybeArray<{
    src: string
    type: VideoType
  }>
  preload?: 'none' | 'metadata' | 'auto'
  alt?: string
  poster?: string
  controls?: boolean
  playsinline?: boolean
  loop?: boolean
  muted?: boolean
  autoplay?: boolean

  width?: number | `${number}`
  height?: number | `${number}`
}

const props = withDefaults(defineProps<VVideoProps>(), {
  preload: 'metadata',
  alt: undefined,
  poster: undefined,
  controls: true,
  width: undefined,
  height: undefined
})

const getExtension = (type: VideoType) => {
  if (type === 'video/mp4') {
    return '.mp4'
  } else {
    return '.webm'
  }
}

const img = useImage()

const computedPoster = computed(() => {
  let imageLink
  if (Array.isArray(props.videos)) {
    imageLink = `${props.videos[0].src}/poster.webp`
  } else {
    imageLink = `${props.videos.src}/poster.webp`
  }

  return img(imageLink, {
    format: 'webp',
    quality: 80,
    width: props.width ? Number(props.width) * 2 : undefined,
    height: props.height ? Number(props.height) * 2 : undefined
  })
})

const isSafariBrowser = ref(false)
onMounted(() => {
  if (import.meta.client) {
    isSafariBrowser.value = isSafari()
  }
})
</script>

<template>
  <video
    :poster="computedPoster"
    :controls
    :width
    :height
    :preload
    :playsinline
    :loop
    :muted
    :autoplay
    class="video"
    :class="isSafariBrowser && 'no-outline safari-subtitles rounded-none!'"
  >
    <template v-if="Array.isArray(videos)">
      <template v-for="video in videos" :key="video.src">
        <source
          :src="`${video.src}/video${getExtension(video.type)}`"
          :type="video.type"
        />

        <track
          kind="subtitles"
          :src="`${video.src}/subtitles-nl.vtt`"
          srclang="nl"
          label="Nederlands"
        />
        <track
          kind="subtitles"
          :src="`${video.src}/subtitles-en.vtt`"
          srclang="en"
          label="English"
        />
      </template>
    </template>
    <template v-else>
      <source
        :src="`${videos.src}/video${getExtension(videos.type)}`"
        :type="videos.type"
      />

      <track
        kind="subtitles"
        :src="`${videos.src}/subtitles-nl.vtt`"
        srclang="nl"
        label="Nederlands"
      />
      <track
        kind="subtitles"
        :src="`${videos.src}/subtitles-en.vtt`"
        srclang="en"
        label="English"
      />
    </template>
  </video>
</template>

<style scoped></style>
